import { type ReactNode } from 'react';

/**
 * Renders `text` with every case-insensitive occurrence of `query` wrapped in
 * a highlight <mark>. Used to make search results scannable — the matched
 * terms stand out in the result rows.
 *
 * Multi-word queries highlight each whitespace-separated term. Falls back to
 * plain text when the query is empty.
 */
export function HighlightText({
  text,
  query,
}: {
  text: string;
  query: string;
}): ReactNode {
  const terms = query.trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return text;

  // Build one regex that matches any term, escaping regex metacharacters.
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const splitRe = new RegExp(`(${escaped.join('|')})`, 'gi');
  // Separate, non-global regex for testing each part (avoids lastIndex state).
  const isMatch = new RegExp(`^(${escaped.join('|')})$`, 'i');

  const parts = text.split(splitRe);
  return parts.map((part, i) =>
    isMatch.test(part) ? (
      <mark
        key={i}
        className="rounded-sm bg-brand-blue/15 text-text-primary px-0.5"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}
